import { useState } from "react";
import {
  useCreateIupcMutation,
  useCreateIupcTeamMutation,
  useDeleteIupcMutation,
  useGetAdminIupcQuery,
} from "../../features/api/appApi";
import AdminSectionCard, {
  AdminButton,
  AdminEmptyState,
  AdminField,
  AdminFormGrid,
  AdminInput,
  AdminScrollTable,
  AdminSelect,
  AdminStatusBadge,
} from "./AdminSectionCard";

const defaultIupcForm = {
  name: "",
  description: "",
  contestDate: "",
  registrationDeadline: "",
  registrationLink: "",
  totalTeams: 0,
  gubRank: 0,
  status: "upcoming",
};

const defaultTeamForm = {
  iupcId: "",
  name: "",
  member1: "",
  member2: "",
  member3: "",
  coach: "",
  rank: 0,
  solved: 0,
};

const AdminIupcPage = () => {
  const { data: iupcs = [] } = useGetAdminIupcQuery();
  const [createIupc] = useCreateIupcMutation();
  const [deleteIupc] = useDeleteIupcMutation();
  const [createIupcTeam] = useCreateIupcTeamMutation();

  const [iupcForm, setIupcForm] = useState(defaultIupcForm);
  const [teamForm, setTeamForm] = useState(defaultTeamForm);

  return (
    <AdminSectionCard title="IUPC" subtitle="Manage IUPC entries and teams">
      <form
        className="space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          await createIupc(iupcForm);
          setIupcForm(defaultIupcForm);
        }}
      >
        <AdminFormGrid>
          <AdminField label="Name">
            <AdminInput
              placeholder="Name"
              value={iupcForm.name}
              onChange={(e) => setIupcForm({ ...iupcForm, name: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Description">
            <AdminInput
              placeholder="Description"
              value={iupcForm.description}
              onChange={(e) => setIupcForm({ ...iupcForm, description: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Contest date">
            <AdminInput
              type="date"
              value={iupcForm.contestDate}
              onChange={(e) => setIupcForm({ ...iupcForm, contestDate: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Registration deadline">
            <AdminInput
              type="date"
              value={iupcForm.registrationDeadline}
              onChange={(e) =>
                setIupcForm({ ...iupcForm, registrationDeadline: e.target.value })
              }
              required
            />
          </AdminField>

          <AdminField label="Registration link" className="md:col-span-2">
            <AdminInput
              placeholder="Registration link"
              value={iupcForm.registrationLink}
              onChange={(e) => setIupcForm({ ...iupcForm, registrationLink: e.target.value })}
              required
            />
          </AdminField>
        </AdminFormGrid>

        <AdminButton className="w-full md:w-auto" type="submit">
          Create IUPC
        </AdminButton>
      </form>

      <form
        className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          await createIupcTeam({
            ...teamForm,
            rank: Number(teamForm.rank),
            solved: Number(teamForm.solved),
          });
          setTeamForm(defaultTeamForm);
        }}
      >
        <p className="text-sm font-semibold text-slate-700">Add IUPC Team</p>
        <AdminFormGrid>
          <AdminField label="IUPC">
            <AdminSelect
              value={teamForm.iupcId}
              onChange={(e) => setTeamForm({ ...teamForm, iupcId: e.target.value })}
              required
            >
              <option value="">Select IUPC</option>
              {iupcs.map((iupc) => (
                <option key={iupc.id} value={iupc.id}>
                  {iupc.name}
                </option>
              ))}
            </AdminSelect>
          </AdminField>

          <AdminField label="Team name">
            <AdminInput
              placeholder="Team name"
              value={teamForm.name}
              onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Member 1">
            <AdminInput
              placeholder="Member 1"
              value={teamForm.member1}
              onChange={(e) => setTeamForm({ ...teamForm, member1: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Member 2">
            <AdminInput
              placeholder="Member 2"
              value={teamForm.member2}
              onChange={(e) => setTeamForm({ ...teamForm, member2: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Member 3">
            <AdminInput
              placeholder="Member 3"
              value={teamForm.member3}
              onChange={(e) => setTeamForm({ ...teamForm, member3: e.target.value })}
              required
            />
          </AdminField>

          <AdminField label="Coach">
            <AdminInput
              placeholder="Coach"
              value={teamForm.coach}
              onChange={(e) => setTeamForm({ ...teamForm, coach: e.target.value })}
              required
            />
          </AdminField>
        </AdminFormGrid>

        <AdminButton className="w-full md:w-auto" type="submit">
          Add Team
        </AdminButton>
      </form>

      <div className="mt-5">
        {iupcs.length ? (
          <AdminScrollTable>
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Registration</th>
                  <th className="px-4 py-3">Teams</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {iupcs.map((iupc) => (
                  <tr key={iupc.id} className="bg-white hover:bg-slate-50/70">
                    <td className="max-w-[260px] break-words px-4 py-3 font-semibold text-slate-800">
                      {iupc.name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {iupc.contestDate ? new Date(iupc.contestDate).toLocaleDateString() : "-"}
                    </td>
                    <td className="max-w-[240px] break-words px-4 py-3 text-slate-600">
                      {iupc.registrationLink || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <AdminStatusBadge tone="green">
                        {Array.isArray(iupc.teamInfo) ? iupc.teamInfo.length : 0}
                      </AdminStatusBadge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <AdminButton
                        variant="danger"
                        className="h-9 px-3 text-xs"
                        onClick={() => deleteIupc(iupc.id)}
                      >
                        Delete
                      </AdminButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminScrollTable>
        ) : (
          <AdminEmptyState text="No IUPC entries yet." />
        )}
      </div>
    </AdminSectionCard>
  );
};

export default AdminIupcPage;
